import { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogTrigger } from '../../ui/Dialog'
import SupplierTermsOfService from './supplier-terms'
import CelebrantTermsOfService from './celebrant-terms'
import { UserType } from '../../../types/UserTypes'

type UserTypeTermsDialogProps = {
  open: boolean
  setOpen: (open: boolean) => void
  isWithButton?: boolean
  userType?: UserType | null
  onScrollToBottom?: () => void
  showAgreementControls?: boolean
  onContinue?: () => void
}

export default function UserTypeTermsDialog({ 
  open, 
  setOpen, 
  isWithButton = false, 
  userType,
  onScrollToBottom,
  showAgreementControls = false,
  onContinue
}: UserTypeTermsDialogProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [isAgreed, setIsAgreed] = useState(false)

  // Reset scroll state when dialog opens and attach scroll listener
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false)
      setIsAgreed(false)
      
      // Attach scroll listener after a short delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const container = scrollContainerRef.current
        if (container) {
          container.addEventListener('scroll', handleScroll)
        }
      }, 100)

      return () => {
        clearTimeout(timer)
        const container = scrollContainerRef.current
        if (container) {
          container.removeEventListener('scroll', handleScroll)
        }
      }
    }
  }, [open])

  // Handle scroll detection
  const handleScroll = () => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10 // 10px tolerance

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
      onScrollToBottom?.()
    }
  }

  const getTermsContent = () => {
    switch (userType) {
      case 2: // Celebrant - "I'm looking for a supplier"
        return <CelebrantTermsOfService />
      case 3: // Supplier - "I am a supplier"
        return <SupplierTermsOfService />
      default:
        // Show message to select user type first
        return (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">
              Please select your account type first (Supplier/Looking for Supplier)
            </h3>
            <p className="text-gray-600">
              To view the appropriate Terms of Service, please select whether you are looking for a supplier or are a supplier yourself.
            </p>
          </div>
        )
    }
  }

  const getTermsTitle = () => {
    switch (userType) {
      case 2:
        return "Terms of Service - Wedding Celebrant"
      case 3:
        return "Terms of Service - Wedding Supplier"
      default:
        return "Terms of Service - Select Account Type"
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[95%] md:max-w-[50%] max-w-[95%] p-0 bg-gradient-to-br from-gray-50 to-white">
        <div 
          ref={scrollContainerRef}
          className="max-h-[95vh] overflow-y-auto"
        >
          {/* Header Section */}
          <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold">{getTermsTitle()}</h1>
                <p className="text-orange-100 text-sm">Please read through the entire document</p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-6 space-y-6">
            {getTermsContent()}
            
            {/* Reading Progress Indicator */}
            {!hasScrolledToBottom && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-blue-800 font-medium">Keep scrolling to continue</p>
                    <p className="text-blue-600 text-sm">Please read through all sections before proceeding</p>
                  </div>
                </div>
              </div>
            )}

            {/* Completion Confirmation */}
            {hasScrolledToBottom && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-green-800 font-semibold text-lg">Terms of Service Read</h3>
                    <p className="text-green-700 text-sm">You have successfully read through the terms of service. You can now proceed with your agreement.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer Agreement Controls */}
          {showAgreementControls && (
            <div className="sticky bottom-0 z-10 border-t bg-white/80 backdrop-blur p-4">
              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    checked={isAgreed}
                    onChange={(e) => setIsAgreed(e.target.checked)}
                  />
                  <span>I agree to the Terms of Service</span>
                </label>
                <button
                  disabled={!isAgreed}
                  onClick={() => {
                    if (!isAgreed) return
                    onContinue?.()
                  }}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-orange-600 hover:to-orange-700 transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
